import { Typography } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import Table from '../../../../components/general/table';
import { ExportExcelProps } from '../../../../helpers/excel';
import moment from 'moment-timezone';
import { ExportPdfProps } from '../../../../helpers/pdf';
import HistoricBalanceForm from '../detail';
import { Invoice } from '../../../../api/models/invoices';
import { useEffect, useState } from 'react';
import { formatMoney } from '../../../../utils/general';
import { get } from '../../../../api';
import { useParams } from 'react-router-dom';

const label = 'statement';
const scrollTable = 2300;

type GenerateTableColumnProps = {
  getColumnSearchProps: (propName: string) => ColumnType<Invoice>;
  showModal: (type: string, id: number | null) => void;
  handleDelete: (id: number) => void;
};

const HistoricBalanceList = (props: any) => {
  const { customerSelected, customerName } = props;
  const { Text } = Typography;
  const [route, setRoute] = useState('/statement');

  let { type } = useParams();
  const titlePdf = `Reporte de antigüedad de saldos ${customerName} : ${customerSelected}`;
  const [extendedRoute, setExtendedRoute] = useState(
    `/customers/${customerSelected}/statement/${
      type === undefined
        ? ''
        : type === 'por-vencer'
        ? '?balance=due'
        : '?balance=overdue'
    }`
  );
  useEffect(() => {
    setExtendedRoute(
      `/customers/${customerSelected}/statement/${
        type === undefined
          ? ''
          : type === 'por-vencer'
          ? '?balance=due'
          : '?balance=overdue'
      }`
    );
  }, [customerSelected, type]);

  const generateTableColumns: (
    props: GenerateTableColumnProps
  ) => ColumnsType<Invoice> = (props) => {
    const { getColumnSearchProps, showModal } = props;

    return [
      {
        title: '# Legal',
        dataIndex: 'legal_number',
        key: 'legal_number',
        ...getColumnSearchProps('legal_number'),
      },
      {
        title: 'Factura',
        dataIndex: 'id',
        key: 'id',
        ...getColumnSearchProps('id'),
      },
      {
        title: 'F. Factura',
        dataIndex: 'issue_date',
        key: 'issue_date',
        render: (issue_date) => (
          <>{moment.tz(issue_date, 'UTC').format('DD/MM/YYYY')}</>
        ),
      },

      {
        title: 'Plazo en días',
        dataIndex: 'days',
        key: 'days',
        ...getColumnSearchProps('days'),
      },
      {
        title: 'F. Vencimiento',
        dataIndex: 'due_date',
        key: 'due_date',
        render: (due_date) => (
          <>{moment.tz(due_date, 'UTC').format('DD/MM/YYYY')}</>
        ),
      },
      {
        title: 'Monto',
        dataIndex: 'total',
        key: 'total',
        ...getColumnSearchProps('total'),
        render: (total) => <Text>$ {formatMoney(total)}</Text>,
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
        key: 'balance',
        ...getColumnSearchProps('balance'),
        render: (balance) => <Text>$ {formatMoney(balance)}</Text>,
      },
      {
        title: 'Por vencer',
        dataIndex: 'to_be_due',
        key: 'to_be_due',
        ...getColumnSearchProps('to_be_due'),
        render: (to_be_due) => <Text>$ {formatMoney(to_be_due)}</Text>,
      },
      {
        title: '1-30',
        dataIndex: 'first_month',
        key: 'first_month',
        ...getColumnSearchProps('first_month'),
        render: (first_month) => <Text>$ {formatMoney(first_month)}</Text>,
      },
      {
        title: '31-60',
        dataIndex: 'second_month',
        key: 'second_month',
        ...getColumnSearchProps('second_month'),
        render: (second_month) => <Text>$ {formatMoney(second_month)}</Text>,
      },
      {
        title: '61-90',
        dataIndex: 'third_month',
        key: 'third_month',
        ...getColumnSearchProps('third_month'),
        render: (third_month) => <Text>$ {formatMoney(third_month)}</Text>,
      },
      {
        title: '>91',
        dataIndex: 'fourth_month',
        key: 'fourth_month',
        ...getColumnSearchProps('fourth_month'),
        render: (fourth_month) => <Text>$ {formatMoney(fourth_month)}</Text>,
      },
      {
        title: 'Días vencidos',
        dataIndex: 'overdue_days',
        key: 'overdue_days',
        ...getColumnSearchProps('overdue_days'),
      },
      // {
      //   title: 'Agente Cobranza',
      //   dataIndex: 'customer_ARAgent_c',
      //   key: 'customer_ARAgent_c',
      //   ...getColumnSearchProps('customer_ARAgent_c'),
      // },
      {
        title: 'Agente Ventas',
        dataIndex: 'sales_agent',
        key: 'sales_agent',
        ...getColumnSearchProps('sales_agent'),
      },
    ];
  };

  const generateExportExcelProps: () => ExportExcelProps = () => ({
    fileName: 'Reporte_Antiguedad_de_Saldos',
    sheetName: 'Antigüedad de saldos',
    tableName: `Tabla de antigüedad de saldos ${customerName} : ${customerSelected}`,
    columnNames: [
      { label: '# Legal', key: 'legal_number' },
      { label: 'Factura', key: 'id' },
      {
        label: 'F. Factura',
        key: 'issue_date',
        format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
      },
      { label: 'Plazo en días', key: 'days' },
      {
        label: 'F. Vencimiento',
        key: 'due_date',
        format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
      },
      {
        label: 'Monto',
        key: 'total',
      },
      {
        label: 'Balance',
        key: 'balance',
      },
      {
        label: 'Por vencer',
        key: 'to_be_due',
      },
      {
        label: '1-30',
        key: 'first_month',
      },
      {
        label: '31-60',
        key: 'second_month',
      },
      {
        label: '61-90',
        key: 'third_month',
      },
      {
        label: '>91',
        key: 'fourth_month',
      },
      { label: 'Días', key: 'overdue_days' },
      // { label: 'Agente Cobranza', key: 'customer_ARAgent_c' },
      { label: 'Agente Ventas', key: 'sales_agent' },
    ],
  });

  const pdfColumns: ExportPdfProps[] = [
    { header: '# Legal', key: 'legal_number' },
    { header: 'Factura', key: 'id' },
    {
      header: 'F. Factura',
      key: 'issue_date',
      format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
    },
    { header: 'P. Días', key: 'days' },
    {
      header: 'F. Vencimiento',
      key: 'due_date',
      format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
    },
    {
      header: 'Monto',
      key: 'total',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: 'Balance',
      key: 'balance',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: 'Por vencer',
      key: 'to_be_due',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: '1-30',
      key: 'first_month',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: '31-60',
      key: 'second_month',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: '61-90',
      key: 'third_month',
      format: (val) => `$${formatMoney(val)}`,
    },
    {
      header: '>91',
      key: 'fourth_month',
      format: (val) => `$${formatMoney(val)}`,
    },
    { header: 'Días', key: 'overdue_days' },
    // { header: 'Agente Cobranza', key: 'customer_ARAgent_c' },
    { header: 'Agente Ventas', key: 'sales_agent' },
  ];

  const invoiceTableProps = {
    label,
    route,
    extendedRoute,
    generateTableColumns,
    generateExportExcelProps,
    detailForm: HistoricBalanceForm,
    pdfColumns,
    titlePdf,
    scrollTable,
  };
  const InvoicesTable = Table<Invoice>(invoiceTableProps);

  return InvoicesTable;
};

export default HistoricBalanceList;
